/**
 * Suggest block
 */

define((require) => {
    'use strict';

    const $ = require('jQuery');
    const toolkit = require('toolkit/b-toolkit/b-toolkit');
    const KEYS = {
        UP: 38,
        DOWN: 40,
        ESC: 27,
        ENTER: 13,
        TAB: 9,
        END: 35,
        HOME: 36,
    };

    return toolkit.create('suggest', {
        /**
         * Minimum number of characters (>=1) a user must type before a search is performed.
         * {Number | Number[]}
         */
        inputLength: 3,

        /**
         * Name of the block, that is responsible of rendering suggest items
         */
        itemBlock: '',

        /**
         * Link to input element
         */
        inputElement: '',

        /**
         * Required callback function to connect any data source to autoComplete.
         * The callback gets two arguments: term, which refers to the value currently in the text input.
         * A suggest callback, which expects a single argument: the data to suggest to the user.
         * This data must be an array of filtered suggestions based on the provided term:
         * ['suggestion 1', 'suggestion 2', 'suggestion 3', ...]
         *
         * @callback
         * @abstract
         * @param {String} term
         * @param {Function} suggest
         */
        source() {},

        /**
         * A callback function that fires when a suggestion is selected by mouse click, enter, or tab.
         * event is the event that triggered the callback, term is the selected value.
         * and item is the item rendered by the renderItem function.
         *
         * @callback
         * @abstract
         * @param {Event} e
         * @param {String} term
         * @param {HTMLElement} item
         */
        onSelect() {},

        /**
         * Update suggest container position and scroll to next element
         *
         * @param {jQuery} [next] - the element to scroll to
         * @private
         */
        _updateSuggestContainer(next) {
            const input = this.inputElement;
            const container = this._container;
            let scrollTop;
            let selectTop;

            container
                .css({
                    top: input.offset().top + input.outerHeight(),
                    left: input.offset().left,
                    width: input.outerWidth(),
                })
                .show();

            if (!container.maxHeight) {
                container.maxHeight = parseInt(container.css('max-height'), 10);
            }

            if (!container.suggestionHeight) {
                container.suggestionHeight = container
                    .find('.' + this.itemBlock)
                    .first()
                    .outerHeight();
            }

            if (next && container.suggestionHeight) {
                if (!next.length) {
                    container.scrollTop(0);
                } else {
                    scrollTop = container.scrollTop();
                    selectTop = next.offset().top - container.offset().top;

                    if (
                        selectTop +
                            container.suggestionHeight -
                            container.maxHeight >
                        0
                    ) {
                        container.scrollTop(
                            selectTop +
                                container.suggestionHeight +
                                scrollTop -
                                container.maxHeight,
                        );
                    } else if (selectTop < 0) {
                        container.scrollTop(selectTop + scrollTop);
                    }
                }
            }
        },

        /**
         * Update suggestions
         *
         * @param {Array} data - data for suggestions
         * @private
         */
        _updateSuggestions(data) {
            const container = this._container;
            const itemBlock = this.itemBlock;
            const emptySuggestBlock = this.emptySuggestBlock;
            let emptySuggest;

            container.empty();

            if (data.length) {
                data.forEach((item) => {
                    const suggestion = toolkit['b-toolkit'].createBlock(
                        document.createElement('div'),
                        {
                            bem: itemBlock,
                            model: item,
                        },
                        'app',
                        'replace',
                    );

                    suggestion.render();
                    container.append(suggestion.el);
                });

                this._updateSuggestContainer();
            } else {
                emptySuggest = toolkit['b-toolkit'].createBlock(
                    document.createElement('div'),
                    {
                        bem: emptySuggestBlock,
                    },
                    'app',
                    'replace',
                );

                emptySuggest.render();
                container.append(emptySuggest.el);
                this._updateSuggestContainer();
            }
        },

        /**
         * Unselect suggestion
         *
         * @private
         */
        _removeSelectedClass() {
            toolkit.toggleMod(this, 'selected', false);
        },

        /**
         * Select suggestion
         *
         * @param {Event} event
         * @private
         */
        _setSelectedClassOnEntered(event) {
            const selectedClass = '.' + this.itemBlock + '_selected';
            const selected = this._container.find(selectedClass);

            if (selected.length) {
                toolkit.toggleMod(selected, 'selected', false);
            }

            toolkit.toggleMod(event.currentTarget, 'selected', true);
        },

        /**
         * Choose suggestion
         *
         * @param {Event} event - original event
         * @param {jQuery} selected - selected suggestion
         * @returns {boolean}
         * @private
         */
        _chooseSuggestion(event, selected) {
            let suggestionValue;

            if (selected.length && this._container.is(':visible')) {
                suggestionValue = selected.getBlock().getValue();

                this.onSelect(event, suggestionValue, selected);

                this._hideContainer();
            }
        },

        /**
         * Suggestion click handler
         *
         * @param {Event} event
         * @returns {boolean}
         * @private
         */
        _onClickSuggestion(event) {
            if (this._blurTimeout) {
                clearTimeout(this._blurTimeout);
            }

            const suggestion = $(event.currentTarget);

            this._chooseSuggestion(event, suggestion);

            return false;
        },

        _navigateSuggestions() {
            let next;
            const container = this._container;
            const itemClass = '.' + this.itemBlock;
            const selectedItemClass = itemClass + '_selected';

            const selected = container.find(selectedItemClass);

            if (!selected.length) {
                next =
                    window.event.which === KEYS.DOWN
                        ? container.find(itemClass).first()
                        : container.find(itemClass).last();

                toolkit.toggleMod(next, 'selected', true);
            } else {
                next =
                    window.event.which === KEYS.DOWN
                        ? selected.next(itemClass)
                        : selected.prev(itemClass);

                if (next.length) {
                    toolkit.toggleMod(selected, 'selected', false);
                    toolkit.toggleMod(next, 'selected', true);
                } else {
                    toolkit.toggleMod(selected, 'selected', false);
                }
            }

            this._updateSuggestContainer(next);
        },

        /**
         * Makes suggest actions depends on control code
         *
         * @param {Event} event
         * @returns {boolean}
         * @private
         */
        _onControlsActions(event) {
            let selected;
            const container = this._container;
            const itemClass = '.' + this.itemBlock;
            const selectedItemClass = itemClass + '_selected';

            if (
                (event.which === KEYS.DOWN || event.which === KEYS.UP) &&
                this._container.children.length
            ) {
                this._navigateSuggestions();

                return false;
            } else if (event.which === KEYS.ESC) {
                if (container.is(':visible')) {
                    event.stopPropagation();
                }

                this._hideContainer();
            } else if (event.which === KEYS.ENTER || event.which === KEYS.TAB) {
                event.preventDefault();

                selected = container.find(selectedItemClass);

                this._chooseSuggestion(event, selected);
            }
        },

        /**
         * Hide suggest when input text to short
         *
         * @param {Event} event
         * @private
         */
        _checkUserInput(event) {
            let inputValue;
            let matchLength = false;
            const specialKeys = [
                KEYS.UP,
                KEYS.DOWN,
                KEYS.ESC,
                KEYS.ENTER,
                KEYS.TAB,
                KEYS.END,
            ];

            if (specialKeys.includes(event.which)) {
                inputValue = this.inputElement.val();

                if (Array.isArray(this.inputLength)) {
                    matchLength = this.inputLength.includes(inputValue.length);
                } else {
                    matchLength = inputValue.length < this.inputLength;
                }

                if (!matchLength) {
                    this._hideContainer();
                }
            }
        },

        /**
         * Setup timeout to hide suggest container
         *
         * @private
         */
        _onInputBlur() {
            this._blurTimeout = setTimeout(this._hideContainer.bind(this), 200);
        },

        /**
         * Hide suggest container
         *
         * @private
         */
        _hideContainer() {
            this._container.hide();
        },

        /**
         * Set event listeners
         *
         * @private
         */
        _setListeners() {
            const itemClass = '.' + this.itemBlock;
            const container = this._container;

            container.on('resize', this._updateSuggestContainer.bind(this));

            container.on('mouseleave', itemClass, this._removeSelectedClass);
            container.on(
                'mouseenter',
                itemClass,
                this._setSelectedClassOnEntered.bind(this),
            );
            container.on(
                'click',
                itemClass,
                this._onClickSuggestion.bind(this),
            );

            this.inputElement.on('blur', this._onInputBlur.bind(this));
            this.inputElement.on('keydown', this._onControlsActions.bind(this));
            this.inputElement.on('keyup', this._checkUserInput.bind(this));
        },

        /**
         * Update suggestions
         *
         * @param {Array} data
         */
        updateSuggestions(data) {
            this._updateSuggestions(data);
        },

        /**
         * Create suggest
         */
        onRender() {
            this._blurTimeout = null;
            this._container = this.$('__container');

            this.inputElement.attr('autocomplete', 'off');

            this._container.appendTo('body');
            this._setListeners();
        },

        destroy() {
            clearTimeout(this._blurTimeout);
            this._container.remove();

            // eslint-disable-next-line prefer-rest-params
            toolkit['b-toolkit'].fn.destroy.apply(this, arguments);
        },
    });
});
